import React, { createContext, useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { kotService } from '../services/kotService';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {

  // --- STATE PERSISTENCE ---
  // ✅ Orders no longer persist to localStorage - always start fresh from API
  const [orders, setOrders] = useState([]);
  const [isBillPrinted, setIsBillPrinted] = useState(false);

  const markBillPrinted = () => {
  setIsBillPrinted(true);
};

const resetBillPrinted = () => {
  setIsBillPrinted(false);
};


  const [completedOrders, setCompletedOrders] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_completedOrders');
      const parsedData = localData ? JSON.parse(localData) : [];
      return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
      return [];
    }
  });

  const [deletionLog, setDeletionLog] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_deletionLog');
      const parsedData = localData ? JSON.parse(localData) : [];
      return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
      return [];
    }
  });

  const [reservations, setReservations] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_reservations');
      const parsedData = localData ? JSON.parse(localData) : [];
      return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
      return [];
    }
  });

  const [waitingList, setWaitingList] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_waitingList');
      const parsedData = localData ? JSON.parse(localData) : [];
      return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
      return [];
    }
  });

  const [waiters, setWaiters] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_waiters');
      const parsedData = localData ? JSON.parse(localData) : [
        { id: 1, name: 'Rahul', active: true, tables: [] },
        { id: 2, name: 'Priya', active: true, tables: [] },
        { id: 3, name: 'Amit', active: true, tables: [] },
        { id: 4, name: 'Sneha', active: true, tables: [] }
      ];
      return parsedData;
    } catch (error) {
      return [
        { id: 1, name: 'Rahul', active: true, tables: [] },
        { id: 2, name: 'Priya', active: true, tables: [] }
      ];
    }
  });

  const [currentTableId, setCurrentTableId] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_currentTableId');
      return localData ? JSON.parse(localData) : null;
    } catch (error) {
      return null;
    }
  });

  const [currenttableSplit, setCurrenttableSplit] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_currenttableSplit');
      return localData ? JSON.parse(localData) : null;
    } catch (error) {
      return null;
    }
  });

  const [orderStatus, setOrderStatus] = useState(() => {
    try {
        const localData = localStorage.getItem('pos_orderStatus');
        return localData ? JSON.parse(localData) : 'ACTIVE';
    } catch (error) {
        return 'ACTIVE';
    }
  });

  // ✅ Refresh counter to trigger re-fetch in useKOT hooks when orders change
  const [ordersRefreshCounter, setOrdersRefreshCounter] = useState(0);
  const triggerOrdersRefresh = () => setOrdersRefreshCounter(prev => prev + 1);

  // ✅ Linked Customers & Waiters per table/split (persisted)
  // Format: { "tableId-split": { customer/waiter object } }
  const [linkedCustomers, setLinkedCustomers] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_linkedCustomers');
      return localData ? JSON.parse(localData) : {};
    } catch (error) {
      return {};
    }
  });

  const [linkedWaiters, setLinkedWaiters] = useState(() => {
    try {
      const localData = localStorage.getItem('pos_linkedWaiters');
      return localData ? JSON.parse(localData) : {};
    } catch (error) {
      return {};
    }
  });

  // --- PERSISTENCE EFFECTS ---
  // ✅ Orders no longer saved to localStorage - rely on API only

  useEffect(() => {
    localStorage.setItem('pos_completedOrders', JSON.stringify(completedOrders));
  }, [completedOrders]);

  useEffect(() => {
    localStorage.setItem('pos_deletionLog', JSON.stringify(deletionLog));
  }, [deletionLog]);

  useEffect(() => {
    localStorage.setItem('pos_reservations', JSON.stringify(reservations));
  }, [reservations]);

  useEffect(() => {
    localStorage.setItem('pos_waitingList', JSON.stringify(waitingList));
  }, [waitingList]);

  useEffect(() => {
    localStorage.setItem('pos_waiters', JSON.stringify(waiters));
  }, [waiters]);

  useEffect(() => {
    localStorage.setItem('pos_currentTableId', JSON.stringify(currentTableId));
    localStorage.setItem('pos_currenttableSplit', JSON.stringify(currenttableSplit));
    const currentOrder = getCurrentOrder();
    if (currentOrder) {
        setOrderStatus(currentOrder.status);
    } else if (currentTableId === null) {
        setOrderStatus('INACTIVE');
    }
  }, [currentTableId, currenttableSplit, orders]);

  // ✅ Persist linked customers and waiters
  useEffect(() => {
    localStorage.setItem('pos_linkedCustomers', JSON.stringify(linkedCustomers));
  }, [linkedCustomers]);

  useEffect(() => {
    localStorage.setItem('pos_linkedWaiters', JSON.stringify(linkedWaiters));
  }, [linkedWaiters]);

  useEffect(() => {
    localStorage.setItem('pos_orderStatus', JSON.stringify(orderStatus));
  }, [orderStatus]);

  // ✅ Reset isBillPrinted when table or split changes
  useEffect(() => {
    setIsBillPrinted(false);
  }, [currentTableId, currenttableSplit]);

  // --- DERIVED STATE ---
  const activeOrders = useMemo(() => {
    return orders.filter(o => o.status === 'ACTIVE' || o.status === 'HOLD');
  }, [orders]);

  const getActiveTableIds = useMemo(() => {
    return activeOrders.map(order => order.tableId);
  }, [activeOrders]);

  // ✅ Get current table/split key for linking
  const getTableSplitKey = (tableId, split) => {
    return `${tableId}-${split || 'A'}`;
  };

  // ✅ Get linked customer for current table/split
  const linkedCustomer = useMemo(() => {
    if (!currentTableId) return null;
    const key = getTableSplitKey(currentTableId, currenttableSplit);
    return linkedCustomers[key] || null;
  }, [currentTableId, currenttableSplit, linkedCustomers]);

  // ✅ Get linked waiter for current table/split
  const linkedWaiter = useMemo(() => {
    if (!currentTableId) return null;
    const key = getTableSplitKey(currentTableId, currenttableSplit);
    return linkedWaiters[key] || null;
  }, [currentTableId, currenttableSplit, linkedWaiters]);

  // ✅ Set linked customer for current table/split
  const setLinkedCustomer = (customer) => {
    if (!currentTableId) return;
    const key = getTableSplitKey(currentTableId, currenttableSplit);
    setLinkedCustomers(prev => {
      if (customer === null) {
        // Remove the entry
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: customer };
    });
  };

  // ✅ Set linked waiter for current table/split
  const setLinkedWaiter = (waiter) => {
    if (!currentTableId) return;
    const key = getTableSplitKey(currentTableId, currenttableSplit);
    setLinkedWaiters(prev => {
      if (waiter === null) {
        // Remove the entry
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: waiter };
    });
  };

  // ✅ Clear linked customer/waiter for a specific table/split (call after bill finalization)
  const clearLinkedCustomerAndWaiter = (tableId, split) => {
    const key = getTableSplitKey(tableId, split);
    setLinkedCustomers(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    setLinkedWaiters(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  // --- HELPER FUNCTIONS ---
  const getNexttableSplit = (tableId) => {
    const tableOrders = orders.filter(o => o.tableId === tableId && (o.status === 'ACTIVE' || o.status === 'HOLD'));
    if (tableOrders.length === 0) return null;
    const labels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
    const usedLabels = tableOrders.map(o => o.tableSplit).filter(Boolean);
    if (usedLabels.length === 0) return 'B';
    for (let label of labels) {
      if (!usedLabels.includes(label)) return label;
    }
    return null;
  };

  const getElapsedTime = (createdAt) => {
    if (!createdAt) return null;
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
    return `${diffMins}m`;
  };

  const getTableStatus = (table, orders) => {
    const tableOrders = orders.filter(o => o.tableId === table.id && (o.status === 'ACTIVE' || o.status === 'HOLD'));
    if (tableOrders.length === 0) return { status: 'AVAILABLE', label: 'Available', color: 'green' };
    const hasHold = tableOrders.some(o => o.status === 'HOLD');
    const hasActive = tableOrders.some(o => o.status === 'ACTIVE');
    if (hasHold && !hasActive) return { status: 'HELD', label: 'On Hold', color: 'orange' };
    if (hasActive) {
      return {
        status: 'ACTIVE',
        label: 'Running',
        color: 'blue',
        elapsedTime: getElapsedTime(tableOrders[0].createdAt),
        total: tableOrders.reduce((sum, o) => sum + o.items.reduce((s, item) => s + ((item.finalPrice || item.price) * item.quantity), 0), 0),
        splits: tableOrders.length
      };
    }
    return { status: 'AVAILABLE', label: 'Available', color: 'green' };
  };

  // --- CORE ORDER FUNCTIONS ---
  const getCurrentOrder = () => {
    if (!currentTableId) return null;
    return orders.find(order => order.tableId === currentTableId && order.tableSplit === currenttableSplit);
  };

  const getTableOrders = (tableId, apiTable = null) => {
  // Local orders
  const localOrders = orders.filter(order => order.tableId === tableId);

  if (!apiTable?.split) return localOrders;

  // Flatten all runningOrders inside splits
  const splitOrders = apiTable.split.flatMap(split => {
    if (!split.runningOrders || split.runningOrders.length === 0) {
      // If split has no running orders, still create placeholder
      return [{
        tableId,
        tableSplit: split.splitName || null,
        pax: split.peopleCount || 0,
        items: [],
        createdAt: new Date().toISOString(),
        status: split.status || 'ACTIVE',
        runningAmount: 0
      }];
    }

    return split.runningOrders.map(ro => ({
      tableId,
      tableSplit: split.splitName || null,
      pax: ro.totalPax || split.peopleCount || 0,
      items: ro.items || [],
      createdAt: ro.startTimestamp || new Date().toISOString(),
      status: split.status || 'ACTIVE',
      runningAmount: ro.runningAmount || 0
    }));
  });

  return [...localOrders, ...splitOrders];
};



  const getCurrentCartItems = () => {
    const currentOrder = getCurrentOrder();
    return currentOrder ? currentOrder.items : [];
  };

  const getRecentOrders = () => {
    return completedOrders.slice(0, 20);
  };

  // FIXED: Normalize table properties to work with API response
  const createNewOrder = (table, pax, forceSplit = false, initialSplit = null, forceCreate = false) => {
        // resetBillPrinted();
    // Ensure table.id is a number (from API it should be)
    const tableId = typeof table.id === 'string' ? parseInt(table.id) : table.id;

    const tableOrders = orders.filter(order =>
      order.tableId === tableId && (order.status === 'HOLD' || order.status === 'ACTIVE')
    );

    // ✅ FORCE SPLIT: Creating a new split on an occupied table (from POSScreen)
    if (tableOrders.length > 0 && forceSplit) {
      const nextLabel = getNexttableSplit(tableId);
      if (!nextLabel) {
        toast.error('Maximum splits reached for this table');
        return;
      }
      if (tableOrders.every(o => !o.tableSplit)) {
        setOrders(prevOrders => prevOrders.map(order => {
          if (order.tableId === tableId && (order.status === 'HOLD' || order.status === 'ACTIVE')) {
            return { ...order, tableSplit: 'A', tableName: `${table.name}-A` };
          }
          return order;
        }));
      }

      // FIXED: Use correct property names from API
      const newOrder = {
        tableId: tableId,
        tableName: `${table.name}-${nextLabel}`, // table.name from processed data
        tableSplit: nextLabel,
        tableType: table.type || 'TABLE', // Default to TABLE if type not provided
        section: table.section, // Already normalized in useTables hook
        tableNumber: table.tableNumber || table.name,
        capacity: table.capacity,
        pax: pax,
        items: [],
        kots: [],
        orderNotes: null,
        waiterId: null,
        status: 'ACTIVE',
        createdAt: new Date().toISOString(),
      };
      setOrders(prevOrders => [...prevOrders, newOrder]);
      setCurrentTableId(tableId);
      setCurrenttableSplit(nextLabel);
      setOrderStatus('ACTIVE');
      return;
    }

    // ✅ Use initialSplit if provided (e.g., 'A' when Split A is created in backend)
    const splitValue = initialSplit || null;

    // ✅ For Counter mode (forceCreate=true), remove any existing order first and create fresh
    if (forceCreate) {
      const newOrder = {
        tableId: tableId,
        tableName: splitValue ? `${table.name}-${splitValue}` : table.name,
        tableSplit: splitValue,
        tableType: table.type || 'TABLE',
        section: table.section,
        tableNumber: table.tableNumber || table.name,
        capacity: table.capacity,
        pax: pax,
        items: [],
        kots: [],
        orderNotes: null,
        waiterId: null,
        status: 'ACTIVE',
        createdAt: new Date().toISOString(),
      };
      // Remove any existing order for this table/split and add new one
      setOrders(prevOrders => [
        ...prevOrders.filter(order => !(order.tableId === tableId && order.tableSplit === splitValue)),
        newOrder
      ]);
      setCurrentTableId(tableId);
      setCurrenttableSplit(splitValue);
      setOrderStatus('ACTIVE');
      return;
    }

    // ✅ Check if order already exists for THIS specific table + split combination
    const existingOrder = orders.find(order =>
      order.tableId === tableId &&
      order.tableSplit === splitValue &&
      (order.status === 'HOLD' || order.status === 'ACTIVE')
    );

    if (existingOrder && !forceSplit) {
      setCurrentTableId(tableId);
      setCurrenttableSplit(splitValue);
      return;
    }

    const newOrder = {
      tableId: tableId,
      tableName: splitValue ? `${table.name}-${splitValue}` : table.name,
      tableSplit: splitValue,
      tableType: table.type || 'TABLE',
      section: table.section,
      tableNumber: table.tableNumber || table.name,
      capacity: table.capacity,
      pax: pax,
      items: [],
      kots: [],
      orderNotes: null,
      waiterId: null,
      status: 'ACTIVE',
      createdAt: new Date().toISOString(),
    };
    setOrders(prevOrders => [...prevOrders, newOrder]);
    setCurrentTableId(tableId);
    setCurrenttableSplit(splitValue);
    setOrderStatus('ACTIVE');
  };

  // ✅ NEW: Create order with items already loaded (for API initialization)
  const createOrderWithItems = (table, pax, split, itemsFromAPI) => {
    // resetBillPrinted();
    const tableId = typeof table.id === 'string' ? parseInt(table.id) : table.id;

    // Convert API items to cart items format
    const cartItems = itemsFromAPI.map(item => {
      const cartItem = {
        id: item.id || item.menuItemId || Date.now() + Math.random(),
        name: item.itemName || item.name,
        price: item.unitPrice || item.price,
        finalPrice: item.unitPrice || item.price,
        station: item.station || 'kitchen', // Legacy fallback
        kitchenStationId: item.kitchenStationId || null,
        kitchenStationName: item.kitchenStationName || null,
        category: item.category || 'General',
        cartId: `${item.id}-${Date.now()}-${Math.random()}`,
        quantity: item.quantity || 1,
        customization: item.specialInstructions ? {
          customInstructions: item.specialInstructions
        } : null,
        sentToKitchen: true // ✅ Items from API have already been sent to kitchen
      };
      return cartItem;
    });

    const newOrder = {
      tableId: tableId,
      tableName: split ? `${table.name}-${split}` : table.name,
      tableSplit: split,
      tableType: table.type || 'TABLE',
      section: table.section,
      tableNumber: table.tableNumber || table.name,
      capacity: table.capacity,
      pax: pax,
      items: cartItems,
      kots: [],
      orderNotes: null,
      waiterId: null,
      status: 'ACTIVE',
      createdAt: new Date().toISOString(),
    };

    setOrders(prevOrders => [...prevOrders, newOrder]);

    setCurrentTableId(tableId);
    setCurrenttableSplit(split);
    setOrderStatus('ACTIVE');
  };

  const addToCart = (item, customization = null, showToast = true) => {
    // Table validation
    if (!currentTableId) {
      toast.error('⚠️ Please select a table first', {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#DC2626',
          color: '#fff',
          fontWeight: 'bold',
        },
      });
      return false;
    }

    let itemAdded = false;

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {

        // CRITICAL: Only check for UNSENT (draft) items when looking for duplicates
        const existingDraftItemIndex = order.items.findIndex(cartItem => {
          // Must NOT be sent to kitchen
          if (cartItem.sentToKitchen === true) return false;

          // Must match item ID
          if (cartItem.id !== item.id) return false;

          // ✅ If item has variants, must also match variant ID
          if (item.variantId || cartItem.variantId) {
            if (cartItem.variantId !== item.variantId) return false;
          }

          // If neither has customization, they match
          if (!cartItem.customization && !customization) return true;
          if (!cartItem.customization || !customization) return false;

          // Compare customization objects
          return (
            cartItem.customization.size === customization.size &&
            cartItem.customization.spiceLevel === customization.spiceLevel &&
            cartItem.customization.preparation === customization.preparation &&
            JSON.stringify(cartItem.customization.quickNotes || []) === JSON.stringify(customization.quickNotes || []) &&
            (cartItem.customization.customInstructions || '') === (customization.customInstructions || '') &&
            (cartItem.customization.parentCartId || null) === (customization.parentCartId || null)

          );
        });

        if (existingDraftItemIndex !== -1) {
          // Found matching DRAFT item - increase its quantity
          const updatedItems = [...order.items];
          updatedItems[existingDraftItemIndex] = {
            ...updatedItems[existingDraftItemIndex],
            quantity: updatedItems[existingDraftItemIndex].quantity + 1,
            sentToKitchen: false
          };
          itemAdded = true;

          if (showToast) {
            toast.success(`✓ ${item.name} quantity updated in draft`, {
              duration: 2000,
              position: 'bottom-center',
            });
          }

          return { ...order, items: updatedItems };
        } else {
          // No matching draft item found - create NEW draft item
          const cartId = `${item.id}-${Date.now()}-${Math.random()}`;
          itemAdded = true;

//           if (showToast) {
//             toast.success(`✓ ${item.name} added to cart`, {
//               duration: 2000,
//               position: 'bottom-center',
//             });
//           }

          return {
            ...order,
            items: [
              ...order.items,
              {
                ...item,
                cartId,
                quantity: 1,
                customization: customization,
                finalPrice: item.finalPrice || item.price,
                sentToKitchen: false
              }
            ]
          };
        }
      }
      return order;
    }));

    return itemAdded;
  };

  // --- KOT FUNCTIONS ---

  /**
   * Get the next KOT number for a specific order
   * Each table/split has its own KOT sequence
   * @param {Object} order - Current order object
   * @param {number} activeOrdersCount - Optional count of active orders from API (to handle page refresh scenario)
   */
  const getNextKOTNumber = (order, activeOrdersCount = 0) => {
    if (!order) return 1;
    const localKotsCount = order.kots?.length || 0;
    // Use the higher of local kots count or API active orders count
    return Math.max(localKotsCount, activeOrdersCount) + 1;
  };

  const hasNewItemsForKOT = () => {
    const currentOrder = getCurrentOrder();
    if (!currentOrder || currentOrder.items.length === 0) return false;
    return currentOrder.items.some(item => !item.sentToKitchen);
  };

  /**
   * Helper function to format customization into special instructions
   */
  const formatSpecialInstructions = (customization) => {
    if (!customization) return '';

    const instructions = [];

    if (customization.size) instructions.push(`Size: ${customization.size}`);
    if (customization.spiceLevel) instructions.push(`Spice: ${customization.spiceLevel}`);
    if (customization.preparation) instructions.push(customization.preparation);
    if (customization.quickNotes && customization.quickNotes.length > 0) {
      instructions.push(customization.quickNotes.join(', '));
    }
    if (customization.customInstructions) {
      instructions.push(customization.customInstructions);
    }

    return instructions.join(' | ');
  };

  /**
   * Send KOT to Kitchen - API Integrated Version
   * @param {number} activeOrdersCount - Optional count of active orders from API (for correct KOT numbering after page refresh)
   */
  const sendKOT = async (activeOrdersCount = 0) => {
    if (!currentTableId) {
      toast.error('No active order');
      return false;
    }

    const currentOrder = getCurrentOrder();
    if (!currentOrder || currentOrder.items.length === 0) {
      toast.error('No items to send to kitchen');
      return false;
    }

    const newItems = currentOrder.items.filter(item => !item.sentToKitchen);

    if (newItems.length === 0) {
      toast.error('No new items to send. All items already in kitchen!');
      return false;
    }

    // Calculate table-specific KOT number (considering API active orders for page refresh scenario)
    const kotNumber = getNextKOTNumber(currentOrder, activeOrdersCount);

    // Show loading toast
    const loadingToast = toast.loading('Sending order to kitchen...');

    try {
      // Get waiter name
      const waiter = waiters.find(w => w.id === currentOrder.waiterId);
      const waiterName = waiter ? waiter.name : 'Server';

      // Determine order type based on table type
      let orderType = 'DINE_IN';
      if (currentOrder.tableType === 'TAKEAWAY') {
        orderType = 'TAKE_AWAY';
      } else if (currentOrder.tableType === 'DELIVERY') {
        orderType = 'DELIVERY';
      }

      // Prepare API request payload
      const kotPayload = {
  tableId: currentOrder.tableId,
  customerId: linkedCustomer?.id || null,
  orderType: orderType,
  waiterName: waiterName,
  notes: currentOrder.orderNotes?.chefNotes || null,
  order_status: 'Sent To Kitchen',
  order_type: orderType,
  kotNumber: 'KOT ' + kotNumber,
  tableSplit: currentOrder.tableSplit ? currentOrder.tableSplit : 'A',   
  items: newItems.map(item => ({
    menuItemId: item.menuItemId || item.id,
    variantId: item.variantId || null,
    quantity: item.quantity,
    itemName: item.name || null,
    specialInstructions: formatSpecialInstructions(item.customization),
    unitPrice: parseFloat(item.price || 0),
    totalPrice: parseFloat((item.finalPrice || item.price) * item.quantity)
  }))
};

      // Call API
      const response = await kotService.sendKOT(kotPayload);

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // On success, update local state
      const newKOT = {
        id: response.data?.id || `kot-${Date.now()}`,
        apiOrderId: response.data?.id, 
        kotNumber: kotNumber, 
        timestamp: new Date().toISOString(),
        items: newItems.map(item => ({ ...item })),
        status: 'sent',
        notes: currentOrder.orderNotes?.chefNotes || null
      };

      setOrders(prevOrders => prevOrders.map(order => {
        if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
          return {
            ...order,
            kots: [...(order.kots || []), newKOT],
            items: order.items.map(item => ({
              ...item,
              sentToKitchen: true
            }))
          };
        }
        return order;
      }));

      toast.success(`✓ KOT #${kotNumber} sent to kitchen!`, {
        duration: 2000,
        icon: '👨‍🍳'
      });

      return true;

    } catch (error) {
      // Dismiss loading toast
      toast.dismiss(loadingToast);

      console.error('Failed to send KOT to kitchen:', error);

      // Show error message
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to send order to kitchen';

      toast.error(`❌ ${errorMessage}`, {
        duration: 4000,
        position: 'top-center',
      });

      return false;
    }
  };

  const updateKOTStatus = (kotId, newStatus) => {
    if (!currentTableId) return;

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        return {
          ...order,
          kots: order.kots.map(kot =>
            kot.id === kotId ? { ...kot, status: newStatus } : kot
          )
        };
      }
      return order;
    }));
  };

  const cancelKOT = (kotId, reason) => {
    if (!currentTableId) {
      toast.error('No active order');
      return false;
    }

    const currentOrder = getCurrentOrder();
    if (!currentOrder) {
      toast.error('Order not found');
      return false;
    }

    const kotToCancel = currentOrder.kots?.find(kot => kot.id === kotId);
    if (!kotToCancel) {
      toast.error('KOT not found');
      return false;
    }

    if (kotToCancel.status === 'cancelled') {
      toast.error('This KOT is already cancelled');
      return false;
    }

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        const updatedKOTs = order.kots.map(kot => {
          if (kot.id === kotId) {
            return {
              ...kot,
              status: 'cancelled',
              cancelledAt: new Date().toISOString(),
              cancellationReason: reason || 'No reason provided'
            };
          }
          return kot;
        });

        const cancelledItemCartIds = kotToCancel.items.map(item => item.cartId);
        const updatedItems = order.items.filter(item =>
          !cancelledItemCartIds.includes(item.cartId)
        );

        return {
          ...order,
          kots: updatedKOTs,
          items: updatedItems
        };
      }
      return order;
    }));

    toast.success(`✓ KOT #${kotToCancel.kotNumber} cancelled successfully`, {
      duration: 3000,
      icon: '❌',
      style: {
        background: '#DC2626',
        color: '#fff',
      }
    });

    // ✅ Trigger refresh in all useKOT hooks
    triggerOrdersRefresh();

    return true;
  };

  const getGroupedBillItems = () => {
    // ✅ Get items from CURRENT split only (not all splits)
    const currentOrder = getCurrentOrder();
    if (!currentOrder) return [];

    // ✅ Get all items that have been sent to kitchen for THIS split
    const sentItems = currentOrder.items.filter(item => item.sentToKitchen === true);

    // ✅ Group by item + customization
    const grouped = {};

    sentItems.forEach(item => {
      const customizationKey = JSON.stringify(item.customization || {});
      const key = `${item.id}-${customizationKey}`;

      if (!grouped[key]) {
        grouped[key] = {
          ...item,
          totalQuantity: item.quantity,
          originalCartIds: [item.cartId]
        };
      } else {
        grouped[key].totalQuantity += item.quantity;
        grouped[key].originalCartIds.push(item.cartId);
      }
    });

    return Object.values(grouped);
  };

  const updateItemCustomization = (cartId, newCustomization) => {
    if (!currentTableId) return;

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        return {
          ...order,
          items: order.items.map(item =>
            item.cartId === cartId
              ? { ...item, customization: newCustomization, sentToKitchen: false }
              : item
          )
        };
      }
      return order;
    }));

    toast.success('✓ Item customization updated', {
      duration: 2000,
      position: 'bottom-center',
    });
  };

  const removeFromCart = (cartId, deleteReason = null) => {
  if (!currentTableId) return;
  const currentOrder = getCurrentOrder();
  const itemToDelete = currentOrder?.items.find(item => item.cartId === cartId);

  if (itemToDelete?.sentToKitchen) {
    toast.error('⚠️ Cannot remove items already sent to kitchen. Contact kitchen to cancel.', {
      duration: 3000,
      position: 'bottom-center',
    });
    return;
  }

  // ✅ Cascade: also drop any draft toppings linked to this item
  const linkedToppingCartIds = (currentOrder?.items || [])
    .filter(item => item.customization?.parentCartId === cartId && !item.sentToKitchen)
    .map(item => item.cartId);

  const cartIdsToRemove = [cartId, ...linkedToppingCartIds];

  if (itemToDelete && deleteReason) {
    setDeletionLog(prev => [...prev, {
      ...itemToDelete,
      deleteReason,
      orderId: `${currentTableId}-${currenttableSplit || 'main'}`,
      deletedAt: new Date().toISOString()
    }]);
  }

  setOrders(prevOrders => prevOrders.map(order => {
    if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
      return {
        ...order,
        items: order.items.filter(item => !cartIdsToRemove.includes(item.cartId))
      };
    }
    return order;
  }));
};

  const updateQuantity = (cartId, quantity) => {
    if (!currentTableId) return;

    const currentOrder = getCurrentOrder();
    const itemToUpdate = currentOrder?.items.find(item => item.cartId === cartId);

    if (itemToUpdate?.sentToKitchen) {
      toast.error('⚠️ Cannot modify items already sent to kitchen', {
        duration: 2000,
        position: 'bottom-center',
      });
      return;
    }

    if (quantity <= 0) {
      removeFromCart(cartId);
    } else {
      setOrders(prevOrders => prevOrders.map(order => {
        if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
          return {
            ...order,
            items: order.items.map(item =>
              item.cartId === cartId ? { ...item, quantity, sentToKitchen: false } : item
            )
          };
        }
        return order;
      }));
    }
  };

  // ✅ NEW: Update special instructions for a specific item
  const updateItemInstructions = (cartId, instructions) => {
    if (!currentTableId) return;

    const currentOrder = getCurrentOrder();
    const itemToUpdate = currentOrder?.items.find(item => item.cartId === cartId);

    if (itemToUpdate?.sentToKitchen) {
      toast.error('⚠️ Cannot modify items already sent to kitchen', {
        duration: 2000,
        position: 'bottom-center',
      });
      return;
    }

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        return {
          ...order,
          items: order.items.map(item =>
            item.cartId === cartId
              ? {
                  ...item,
                  customization: {
                    ...(item.customization || {}),
                    customInstructions: instructions
                  },
                  sentToKitchen: false
                }
              : item
          )
        };
      }
      return order;
    }));

    toast.success('✓ Instructions updated', {
      duration: 1500,
    });
  };

  const updateOrderNotes = (notes) => {
    if (!currentTableId) return;
    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        return { ...order, orderNotes: notes };
      }
      return order;
    }));
  };

  const clearCart = () => {
    if (!currentTableId) return;

    const currentOrder = getCurrentOrder();
    const hasSentItems = currentOrder?.items.some(item => item.sentToKitchen);

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        return {
          ...order,
          items: order.items.filter(item => item.sentToKitchen === true)
        };
      }
      return order;
    }));

    if (hasSentItems) {
      toast.success('✓ Draft items cleared. Sent items preserved.', {
        duration: 2000,
        position: 'bottom-center',
      });
    } else {
      toast.success('✓ Cart cleared', {
        duration: 2000,
        position: 'bottom-center',
      });
    }
  };

  const calculateTotal = () => {
    const currentItems = getCurrentCartItems();
    return currentItems.reduce((total, item) => {
      const price = item.finalPrice || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

 const calculateOrderTotal = (order) => {
  return order.runningAmount || order.total_amount || order.amount || 0;
};



  const holdOrder = () => {
    if (!currentTableId) return;
    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
        return { ...order, status: 'HOLD' };
      }
      return order;
    }));
    setCurrentTableId(null);
    setCurrenttableSplit(null);
    setOrderStatus('INACTIVE');
  };

  const switchToOrder = (tableId, tableSplit = null) => {
    // resetBillPrinted();
    // Ensure tableId is a number
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;
    setCurrentTableId(numericTableId);
    setCurrenttableSplit(tableSplit);
    const order = orders.find(o => o.tableId === numericTableId && o.tableSplit === tableSplit);
    if (order) setOrderStatus(order.status);
  };

  const resumeHeldOrder = (tableId, tableSplit = null) => {
    // resetBillPrinted();
    // Ensure tableId is a number
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;
    setCurrentTableId(numericTableId);
    setCurrenttableSplit(tableSplit);
    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === numericTableId && order.tableSplit === tableSplit && order.status === 'HOLD') {
        return { ...order, status: 'ACTIVE' };
      }
      return order;
    }));
    setOrderStatus('ACTIVE');
  };

  const finalizeOrder = () => {
    if (!currentTableId) return;
    const orderToComplete = getCurrentOrder();
    if (orderToComplete) {
      setCompletedOrders(prev => [{
        ...orderToComplete,
        completedAt: new Date().toISOString(),
        status: 'COMPLETED'
      }, ...prev].slice(0, 50));
    }
    const tableIdToRemove = currentTableId;
    const splitToRemove = currenttableSplit;
    setCurrentTableId(null);
    setCurrenttableSplit(null);
    setOrderStatus('INACTIVE');
    setIsBillPrinted(false); 
    setOrders(prevOrders => prevOrders.filter(order =>
      !(order.tableId === tableIdToRemove && order.tableSplit === splitToRemove)
    ));
  };

  const cancelOrder = async (tableId = currentTableId, tableSplit = currenttableSplit) => {
    if (!tableId) {
      toast.error('⚠️ No table specified');
      throw new Error('No table specified');
    }

    // Ensure tableId is a number
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;

    try {
      // Call API to cancel orders and clean up table split
      await kotService.cancelOrdersByTableSplit(numericTableId, tableSplit);

      // Clear current table state if cancelling current table
      if (numericTableId === currentTableId && tableSplit === currenttableSplit) {
        setCurrentTableId(null);
        setCurrenttableSplit(null);
        setOrderStatus('INACTIVE');
      }

      // Remove orders from local state
      setOrders(prevOrders =>
        prevOrders.filter(
          order => !(order.tableId === numericTableId && order.tableSplit === tableSplit)
        )
      );

      // Success - let calling component handle UI feedback
    } catch (error) {
      console.error('Failed to cancel order:', error);
      toast.error('❌ Failed to cancel order: ' + (error.message || 'Unknown error'));
      throw error; // Re-throw so calling component knows it failed
    }
  };

  const repeatOrder = (orderToRepeat) => {
    if (!currentTableId) {
      toast.error('⚠️ Please select a table first');
      return;
    }

    setOrders(prevOrders => {
      const updatedOrders = prevOrders.map(order => {
        if (order.tableId === currentTableId && order.tableSplit === currenttableSplit) {
          const newItems = orderToRepeat.items.map(item => ({
            ...item,
            cartId: Date.now() + Math.random() + Math.random(),
            sentToKitchen: false
          }));
          return { ...order, items: [...order.items, ...newItems] };
        }
        return order;
      });

      return updatedOrders;
    });

    toast.success('✓ Order repeated successfully');
  };

  const getHeldOrders = () => {
    return orders.filter(order => order.status === 'HOLD');
  };

  const updateOrderPax = (tableId, tableSplit, newPax) => {
    if (!tableId) return;
    // Ensure tableId is a number
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === numericTableId && order.tableSplit === tableSplit) {
        return { ...order, pax: newPax };
      }
      return order;
    }));
  };

  // --- RESERVATION FUNCTIONS ---
  const addReservation = (reservation) => {
    const newReservation = {
      ...reservation,
      id: Date.now(),
      status: 'PENDING',
      createdAt: new Date().toISOString()
    };
    setReservations(prev => [...prev, newReservation]);
    return newReservation;
  };

  const updateReservation = (id, updates) => {
    setReservations(prev => prev.map(res =>
      res.id === id ? { ...res, ...updates } : res
    ));
  };

  const cancelReservation = (id) => {
    setReservations(prev => prev.map(res =>
      res.id === id ? { ...res, status: 'CANCELLED' } : res
    ));
  };

  const getActiveReservations = () => {
    return reservations.filter(r => r.status === 'PENDING' || r.status === 'CONFIRMED');
  };

  const getTableReservation = (tableId) => {
    // Ensure tableId is a number for comparison
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;

    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentHour = now.getHours();

    return reservations.find(res =>
      res.tableId === numericTableId &&
      res.date === today &&
      res.status === 'PENDING' &&
      parseInt(res.time.split(':')[0]) >= currentHour &&
      parseInt(res.time.split(':')[0]) <= currentHour + 2
    );
  };

  // --- WAITING LIST FUNCTIONS ---
  const addToWaitingList = (customer) => {
    const newEntry = {
      ...customer,
      id: Date.now(),
      addedAt: new Date().toISOString(),
      position: waitingList.length + 1,
      status: 'WAITING',
      notified: false
    };
    setWaitingList(prev => [...prev, newEntry]);
    return newEntry;
  };

  const notifyWaitingCustomer = (id) => {
    setWaitingList(prev => prev.map(entry =>
      entry.id === id ? { ...entry, notified: true, notifiedAt: new Date().toISOString() } : entry
    ));
  };

  const removeFromWaitingList = (id) => {
    setWaitingList(prev => prev.filter(entry => entry.id !== id));
  };

  const seatedFromWaitingList = (id, tableId) => {
    setWaitingList(prev => prev.map(entry =>
      entry.id === id ? { ...entry, status: 'SEATED', tableId, seatedAt: new Date().toISOString() } : entry
    ));
  };

  // --- WAITER FUNCTIONS ---
  const assignWaiter = (tableId, waiterId) => {
    // Ensure tableId is a number
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;

    setOrders(prevOrders => prevOrders.map(order => {
      if (order.tableId === numericTableId) {
        return { ...order, waiterId };
      }
      return order;
    }));

    setWaiters(prevWaiters => prevWaiters.map(waiter => {
      if (waiter.id === waiterId) {
        return {
          ...waiter,
          tables: [...new Set([...waiter.tables, numericTableId])]
        };
      } else {
        return {
          ...waiter,
          tables: waiter.tables.filter(t => t !== numericTableId)
        };
      }
    }));
  };

  const getWaiterForTable = (tableId) => {
    // Ensure tableId is a number
    const numericTableId = typeof tableId === 'string' ? parseInt(tableId) : tableId;
    const order = orders.find(o => o.tableId === numericTableId);
    if (order?.waiterId) {
      return waiters.find(w => w.id === order.waiterId);
    }
    return null;
  };

  // CLEAR ALL (Testing)
  const clearAllOrders = () => {
    setOrders([]);
    setCurrentTableId(null);
    setCurrenttableSplit(null);
    setOrderStatus('INACTIVE');
    setIsBillPrinted(false); 
    // pos_orders no longer used in localStorage
    localStorage.removeItem('pos_currentTableId');
    localStorage.removeItem('pos_currenttableSplit');
    localStorage.removeItem('pos_orderStatus');
  };

  const value = {
    // Core
    orders,
    currentTableId,
    currenttableSplit,
    getCurrentOrder,
    getCurrentCartItems,
    getTableOrders,
    createNewOrder,
    createOrderWithItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateItemInstructions,
    updateOrderNotes,
    updateItemCustomization,
    clearCart,
    calculateTotal,
    calculateOrderTotal,
    holdOrder,
    switchToOrder,
    resumeHeldOrder,
    finalizeOrder,
    cancelOrder,
    getHeldOrders,
    getActiveTableIds,
    orderStatus,
    setOrderStatus,
    getTableStatus,
    getElapsedTime,
    clearAllOrders,
    getNexttableSplit,
    getRecentOrders,
    repeatOrder,
    deletionLog,
    updateOrderPax,
    ordersRefreshCounter, // ✅ For triggering useKOT refresh
    triggerOrdersRefresh, // ✅ For manually triggering refresh
    // 🔒 Bill Lock
    isBillPrinted,
    markBillPrinted,
    resetBillPrinted,


    // KOT Functions
    sendKOT,
    hasNewItemsForKOT,
    getNextKOTNumber,
    updateKOTStatus,
    cancelKOT,
    getGroupedBillItems,

    // Reservations
    reservations,
    addReservation,
    updateReservation,
    cancelReservation,
    getActiveReservations,
    getTableReservation,

    // Waiting List
    waitingList,
    addToWaitingList,
    notifyWaitingCustomer,
    removeFromWaitingList,
    seatedFromWaitingList,

    // Waiters
    waiters,
    setWaiters,
    assignWaiter,
    getWaiterForTable,

    // Linked Customer & Waiter (for current order in POS)
    linkedCustomer,
    setLinkedCustomer,
    linkedWaiter,
    setLinkedWaiter,
    clearLinkedCustomerAndWaiter,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};